import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CircleNotch, CheckCircle, Package } from "@phosphor-icons/react";
import { postLoad, EQUIPMENT } from "@/lib/api";

export const PostLoadDialog = ({ open, onOpenChange, onPosted }) => {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ equipment: "Dry Van" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["origin_city", "origin_state", "dest_city", "dest_state", "pickup_date", "delivery_date", "rate_usd", "shipper_name", "shipper_email"];
    for (const r of required) {
      if (!form[r]) { toast.error("Please fill all required fields"); return; }
    }
    setLoading(true);
    try {
      const load = await postLoad({
        ...form,
        rate_usd: parseFloat(form.rate_usd),
        weight_lbs: form.weight_lbs ? parseFloat(form.weight_lbs) : null,
        miles: form.miles ? parseFloat(form.miles) : null,
        deadhead: form.deadhead ? parseFloat(form.deadhead) : null,
      });
      setDone(true);
      toast.success("Load posted — it's live on the board!");
      onPosted && onPosted(load);
    } catch {
      toast.error("Could not post load. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setDone(false); setForm({ equipment: "Dry Van" }); }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[#111A2E] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="post-load-dialog">
        {done ? (
          <div className="py-10 text-center flex flex-col items-center gap-4" data-testid="post-load-success">
            <CheckCircle size={56} weight="fill" className="text-[#2563EB]" />
            <h3 className="font-display text-2xl font-bold">Load posted</h3>
            <p className="text-[#94A3B8] max-w-sm">Your load is now live on the Coastal board. Carriers can request it right away.</p>
            <button onClick={() => close(false)} className="mt-2 px-6 py-2.5 rounded-full bg-[#2563EB] text-white font-semibold" data-testid="post-load-close-btn">Done</button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
                <Package size={24} className="text-[#2563EB]" /> Post a Load
              </DialogTitle>
              <DialogDescription className="text-[#94A3B8]">Fill in the lane details — it goes live instantly.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Origin City *"><Inp v={form.origin_city} on={(x) => set("origin_city", x)} tid="pl-origin-city" /></Field>
                <Field label="Origin State *"><Inp v={form.origin_state} on={(x) => set("origin_state", x)} tid="pl-origin-state" ph="TX" /></Field>
                <Field label="Destination City *"><Inp v={form.dest_city} on={(x) => set("dest_city", x)} tid="pl-dest-city" /></Field>
                <Field label="Destination State *"><Inp v={form.dest_state} on={(x) => set("dest_state", x)} tid="pl-dest-state" ph="GA" /></Field>
                <Field label="Pickup Date *"><Inp type="date" v={form.pickup_date} on={(x) => set("pickup_date", x)} tid="pl-pickup" /></Field>
                <Field label="Delivery Date *"><Inp type="date" v={form.delivery_date} on={(x) => set("delivery_date", x)} tid="pl-delivery" /></Field>
                <Field label="Equipment *">
                  <Select value={form.equipment} onValueChange={(v) => set("equipment", v)}>
                    <SelectTrigger data-testid="pl-equipment" className="bg-[#0B1220] border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#111A2E] border-white/10 text-white">
                      {EQUIPMENT.map((eq) => <SelectItem key={eq} value={eq}>{eq}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Rate (USD) *"><Inp type="number" v={form.rate_usd} on={(x) => set("rate_usd", x)} tid="pl-rate" ph="1500" /></Field>
                <Field label="Weight (lbs)"><Inp type="number" v={form.weight_lbs} on={(x) => set("weight_lbs", x)} tid="pl-weight" /></Field>
                <Field label="Miles"><Inp type="number" v={form.miles} on={(x) => set("miles", x)} tid="pl-miles" /></Field>
                <Field label="Commodity"><Inp v={form.commodity} on={(x) => set("commodity", x)} tid="pl-commodity" /></Field>
                <Field label="Deadhead (mi)"><Inp type="number" v={form.deadhead} on={(x) => set("deadhead", x)} tid="pl-deadhead" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Shipper Name *"><Inp v={form.shipper_name} on={(x) => set("shipper_name", x)} tid="pl-shipper-name" /></Field>
                <Field label="Shipper Email *"><Inp type="email" v={form.shipper_email} on={(x) => set("shipper_email", x)} tid="pl-shipper-email" /></Field>
              </div>
              <Field label="Shipper Phone"><Inp v={form.shipper_phone} on={(x) => set("shipper_phone", x)} tid="pl-shipper-phone" /></Field>
              <Field label="Notes"><Textarea data-testid="pl-notes" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className="bg-[#0B1220] border-white/10" rows={2} /></Field>
              <button type="submit" disabled={loading} data-testid="pl-submit-btn" className="w-full py-3 rounded-full bg-[#2563EB] text-white font-semibold hover:bg-[#1D4ED8] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {loading && <CircleNotch size={18} className="animate-spin" />} Post Load
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-[#94A3B8]">{label}</Label>
    {children}
  </div>
);

const Inp = ({ v, on, tid, ph, type = "text" }) => (
  <Input type={type} data-testid={tid} value={v || ""} onChange={(e) => on(e.target.value)} placeholder={ph} className="bg-[#0B1220] border-white/10" />
);
